import { rem } from 'polished';
import { useEffect, useRef, useState } from 'react';

import { getColor } from '@klappir/ui/brand';
import { media } from '@klappir/util/styles';
import styled from '@klappir/vendor/styled';

const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(336px, 1fr));
  grid-column-gap: 40px;
  grid-row-gap: 40px;
  margin-bottom: ${rem(28)};

  ${media.mobile.below`
    grid-row-gap: 24px;
  `}
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const FilterLabel = styled.label`
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${getColor('green')};
  padding: 0 ${rem(4)};
  position: absolute;
  top: 0;
  left: ${rem(12)};
  transform: translateY(-50%);
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background-color: white;
    z-index: -1;
  }

  ${media.mobile.below`
    display: none;
  `}
`;

const Select = styled.select`
  cursor: pointer;
  padding: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${getColor('black')};
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: ${rem(6)};
  min-width: ${rem(200)};

  &:focus {
    outline: none;
    border-color: ${getColor('green')};
  }

  ${media.mobile.below`
    color: ${getColor('green')};
    padding: ${rem(8)} ${rem(16)} ${rem(8)};
  `}
`;

const FilterPopup = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: ${rem(6)};
  margin-top: ${rem(4)};
  max-height: ${rem(300)};
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 ${rem(4)} ${rem(8)} rgba(0, 0, 0, 0.1);
`;

const OptionBullet = styled.span`
  display: inline-block;
  width: ${rem(6)};
  height: ${rem(6)};
  background-color: ${getColor('green')};
  border-radius: 50%;
  margin-right: ${rem(12)};
`;

const Option = styled.div<{ selected?: boolean }>`
  padding: ${rem(8)} ${rem(16)};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div:first-child {
    display: flex;
    align-items: center;
  }

  &:hover {
    background-color: ${getColor('green', 100, 10)};
  }

  ${({ selected }) =>
    selected &&
    `
    background-color: ${getColor('green', 100, 10)};
    font-weight: 600;
  `}
`;

const SelectedCount = styled.span`
  position: absolute;
  right: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  color: ${getColor('green')};
  font-size: ${rem(14)};
  font-weight: 600;
`;

const SelectedValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(4)};
  padding-right: ${rem(24)};
`;

const SelectedValue = styled.span`
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${getColor('black')};

  &:not(:last-child):after {
    content: ',';
    margin-right: ${rem(4)};
  }
`;

const SearchInput = styled.input`
  padding: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${getColor('black')};
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: ${rem(6)};
  width: 100%;
  min-width: ${rem(200)};
  height: ${rem(66)};

  &::placeholder {
    color: ${getColor('black')};
  }

  &:focus {
    outline: none;
    border-color: ${getColor('green')};
  }

  ${media.mobile.below`
    color: ${getColor('green')};
    padding: ${rem(8)} ${rem(16)} ${rem(8)};
    height: ${rem(40)};
  `}
`;

interface FilterOption {
  value: string;
  label: string;
}

interface Filter {
  id: string;
  label: string;
  options: FilterOption[];
  multiple?: boolean;
  sort?: (a: FilterOption, b: FilterOption) => number;
}

interface FiltersProps {
  filters: Filter[];
  values: Record<string, string[]>;
  onChange: (filterId: string, value: string) => void;
}

export const Filters = ({ filters, values, onChange }: FiltersProps) => {
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setOpenFilter(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (filterId: string, value: string) => {
    if (value === '') {
      onChange(filterId, '');
    } else {
      onChange(filterId, value);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('search', e.target.value);
  };

  const getSelectedCount = (filterId: string) => {
    const selectedValues = values[filterId] || [];
    return selectedValues.length ? `(${selectedValues.length})` : '';
  };

  const camelCaseToHumanReadable = (camelCaseString: string): string => {
    return camelCaseString
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getSelectedLabels = (filterId: string, filter: Filter) => {
    const selectedValues = values[filterId] || [];
    if (selectedValues.length === 0) {
      return (
        filter.options.find((opt) => opt.value === '')?.label || 'Select...'
      );
    }

    return (
      <SelectedValues>
        {selectedValues.map((value) => (
          <SelectedValue key={value}>
            {camelCaseToHumanReadable(
              filter.options.find((opt) => opt.value === value)?.label || ''
            )}
          </SelectedValue>
        ))}
      </SelectedValues>
    );
  };

  return (
    <FilterContainer ref={containerRef}>
      {filters.map((filter) => (
        <FilterGroup key={filter.id}>
          <FilterLabel>{filter.label}</FilterLabel>
          {filter.id === 'search' ? (
            <SearchInput
              type="text"
              placeholder="Search..."
              value={values[filter.id]?.[0] || ''}
              onChange={handleSearchChange}
            />
          ) : (
            <Select
              as="div"
              onClick={() =>
                setOpenFilter(openFilter === filter.id ? null : filter.id)
              }
              style={{ position: 'relative', cursor: 'pointer' }}
            >
              {getSelectedLabels(filter.id, filter)}
              <SelectedCount>{getSelectedCount(filter.id)}</SelectedCount>

              {openFilter === filter.id && (
                <FilterPopup>
                  {filter.options
                    .sort(filter.sort || ((a, b) => 0))
                    .map(({ value, label }) => (
                      <Option
                        key={value}
                        selected={
                          value === ''
                            ? values[filter.id]?.length === 0
                            : values[filter.id]?.includes(value)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(filter.id, value);
                        }}
                      >
                        <div>
                          <OptionBullet />
                          {camelCaseToHumanReadable(label)}
                        </div>
                        {(value === ''
                          ? values[filter.id]?.length === 0
                          : values[filter.id]?.includes(value)) && ' ✓'}
                      </Option>
                    ))}
                </FilterPopup>
              )}
            </Select>
          )}
        </FilterGroup>
      ))}
    </FilterContainer>
  );
};
